// Onboarding a tenant, step by step: 1 the tenant, 2 its users, 3 its first
// site. Each step commits immediately — the tenant exists the moment step 1
// succeeds — and steps 2 and 3 are skippable, so backing out mid-flow leaves
// nothing half-made, only less-made. Finish lands on the tenant detail page,
// where every action here remains available; the stepper is a guided path
// through the same forms, not the only door to them.
import { useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router";
import type { AdminTenant } from "@gb/admin-contracts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/gb/field";
import { PageHeader } from "@/components/gb/page-header";
import { Stepper } from "@/components/gb/stepper";
import { SiteCreateForm } from "../projects/site-form";
import { useCreateTenant } from "./tenants-api";
import { UserCreateForm } from "./user-form";

const STEPS = [
  { number: 1, label: "Tenant" },
  { number: 2, label: "Users" },
  { number: 3, label: "First site" },
];

function TenantStep({ onCreated }: { onCreated: (tenant: AdminTenant) => void }) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [domains, setDomains] = useState("");
  const [allowSelfRegistration, setAllowSelfRegistration] = useState(false);
  const create = useCreateTenant();

  const parsedDomains = domains
    .split(",")
    .map((d) => d.trim())
    .filter(Boolean);
  const canSubmit =
    /^[a-z][a-z0-9_]{1,48}$/.test(slug) &&
    Boolean(name.trim()) &&
    parsedDomains.length > 0 &&
    !create.isPending;

  return (
    <form
      className="max-w-[560px] space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        create.mutate(
          {
            slug,
            name: name.trim(),
            emailDomains: parsedDomains,
            config: allowSelfRegistration ? { allowSelfRegistration: true } : undefined,
          },
          { onSuccess: onCreated },
        );
      }}
    >
      <Field
        label="Slug"
        hint="Lowercase letters, digits and _, starting with a letter. Immutable — it names the tenant's schema and storage prefix."
        error={create.isError ? String(create.error.message) : undefined}
      >
        {(props) => (
          <Input
            {...props}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="green_buddies"
            autoComplete="off"
            className="font-mono"
          />
        )}
      </Field>
      <Field label="Name">
        {(props) => (
          <Input
            {...props}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Green Buddies s.r.o."
          />
        )}
      </Field>
      <Field
        label="Email domains"
        hint="Comma-separated. Logins route to this tenant by these domains — at least one."
      >
        {(props) => (
          <Input
            {...props}
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            placeholder="green-buddies.cz, green-buddies.sk"
            autoComplete="off"
          />
        )}
      </Field>
      <label className="flex items-center gap-2 text-[0.85rem] text-ink">
        <input
          type="checkbox"
          checked={allowSelfRegistration}
          onChange={(e) => setAllowSelfRegistration(e.target.checked)}
          className="size-4 accent-brand"
        />
        Allow self-registration on those domains
      </label>
      <Button type="submit" disabled={!canSubmit}>
        {create.isPending ? "Provisioning…" : "Create tenant"}
      </Button>
    </form>
  );
}

export function TenantOnboardingPage() {
  const navigate = useNavigate();
  // The step is local state, not a URL: the tenant id only exists after step 1
  // commits, and a reload mid-flow correctly lands back on the tenant list —
  // everything committed so far is already visible there.
  const [step, setStep] = useState(1);
  const [tenant, setTenant] = useState<AdminTenant | null>(null);

  const finish = (): void => {
    if (tenant) void navigate(`/tenants/${tenant.id}`);
  };

  return (
    <div>
      <div className="mb-1 text-[0.75rem]">
        <Link to="/tenants" className="text-brand hover:underline">
          ← Tenants
        </Link>
      </div>
      <PageHeader
        eyebrow="Onboarding"
        title={tenant ? tenant.name : "New tenant"}
        actions={
          tenant ? (
            <Button variant="secondary" size="sm" onClick={finish}>
              Finish — go to tenant
            </Button>
          ) : undefined
        }
      />

      <Stepper steps={STEPS} current={step} furthest={step} className="mt-6 max-w-[560px]" />

      <div className="mt-8">
        {step === 1 && (
          <TenantStep
            onCreated={(created) => {
              setTenant(created);
              setStep(2);
            }}
          />
        )}

        {step === 2 && tenant && (
          <div className="max-w-[560px]">
            <h2 className="font-display text-[0.85rem] font-semibold tracking-[0.08em] text-muted uppercase">
              Add users
            </h2>
            <p className="mt-1 mb-4 text-[0.8rem] leading-[1.5] text-muted">
              Each user gets a generated password, shown once. You can also do this later from
              the tenant page.
            </p>
            <UserCreateForm tenantId={tenant.id} />
            <div className="mt-6 flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setStep(3)}>
                Continue
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setStep(3)}>
                Skip
              </Button>
            </div>
          </div>
        )}

        {step === 3 && tenant && (
          <div className="max-w-[560px]">
            <h2 className="font-display text-[0.85rem] font-semibold tracking-[0.08em] text-muted uppercase">
              First site
            </h2>
            <p className="mt-1 mb-4 text-[0.8rem] leading-[1.5] text-muted">
              The wizard's basics in one window. Documents, the journal and the Brain are on the
              project page once it exists.
            </p>
            <SiteCreateForm tenantId={tenant.id} onCreated={finish} />
            <div className="mt-6">
              <Button variant="ghost" size="sm" onClick={finish}>
                Skip — finish without a site
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
